<template>
  <modal
    v-if="show"
    height="850px"
    width="920px"
    bodyPadding="0"
    class="offer-details-dialog"
    :title="modalTitle"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${$i18n.locale}/offer-settings`"
  >
    <template #default>
      <div class="dialog-wrapper">
        <div class="details offer-dialog__panel offer-dialog__panel--main">
          <div class="subtitle">
            1. {{t('Offer details')}}
          </div >

          <div class="p-16 campaign-details__wrap">
            <div>
             <workspace-select
               :disabled="mode === 'edit' || disableWorkspaceSelect"
               :form="form"
               @change-workspace="onChangeWorkspace"
               :labelHelpText="t('Workspace allows to make the required offer or other element accessible only by a specific member or group of members')"
               :workspacesList="workspacesList"
               attach
             ></workspace-select>
            </div>

            <div v-bind="getIntroParams(2)">
              <ui-input
                v-model="form.name"
                class="step-z"
                :placeholder="t('Name')"
                :label="t('Name')+'*'"
                :label-help="t('The first part of name is automatically generated basing on selected country.')"
                name="name"
                :hide-details="1"
                :error-messages="errorsDict['name']"
              ></ui-input>
              <p class="name-description">{{ nameDescription }}</p>
            </div>

            <div>
              <ui-select
                v-model="form.countryId"
                :placeholder="t('Country')"
                :label="t('Country')"
                :label-help="t('Country selection is required to navigate easy through the offers list. This selection does not affect the traffic distribution.')"
                :items="countryListPlusGlobal"
                item-text="name"
                item-value="id"
                :return-object="false"
                :hide-details="2"
                attach
                :error-messages="errorsDict['countryId']"
              ></ui-select>
            </div>

            <div :class="{'plan-limits-warning__tags': showPlanLimitsWarning_tags}">
              <ui-select
                planLimits="tagsOffers"
                v-model="form.tags"
                :placeholder="t('Select or type tags')+'...'"
                :label="t('Tags')"
                :label-help="t('Add personalized tags to search and navigate easy through offers list.')"
                :items="store.cache.tags.offers.data"
                item-text="name"
                :hide-details="2"
                multiple
                chips
                attach
                combobox
                multi-line
                :error-messages="errorsDict['tags']"
              >
                <template #no-data>
                  <no-tag-found></no-tag-found>
                </template>
              </ui-select>
              <button v-if="showPlanLimitsWarning_tags" class="plan-limits-warning__clear" @click="clearTags">
                {{t('Reset tags')}}
              </button>
              <ui-tooltip-icon
                class="plan-limits-warning__tooltip"
                preset="error"
                v-if="showPlanLimitsWarning_tags"
                :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
              ></ui-tooltip-icon>
            </div>

            <div>
              <ui-select
                v-model="form.affiliateNetworkId"
                :placeholder="t('Affiliate network')"
                :label="t('Affiliate network')"
                :items="affiliatesList"
                item-text="name"
                item-value="id"
                :return-object="false"
                :hide-details="2"
                attach
                :error-messages="errorsDict['affiliateNetworkId']"
                @change="changeAffiliates"
              ></ui-select>
            </div>

            <div v-bind="getIntroParams(3)">
              <ui-create-url-tags
                v-model="form.url"
                :chips="chips"
                :label="t('URL')+'*'"
                :has-errors="!!errorsDict['url']"
              ></ui-create-url-tags>
            </div>

            <div>
              <ui-copy-input v-model="postbackUrl" :label="t('Postback URL')"></ui-copy-input>
            </div>
          </div>
        </div>

        <div class="details offer-dialog__panel offer-dialog__panel--advanced">
          <div class="subtitle">
            2. {{t('Advanced options')}}
          </div>

          <div class="p-16 campaign-details__wrap">
            <div>
              <ui-input
                v-if="!clickIdParam"
                v-model="form.clickIdParam"
                :label="t('Click ID parameter')"
                :label-help="t('With this HTTP GET parameter BeMob Click ID will be passed to affiliate network.')"
                :hide-details="2"
                :error-messages="errorsDict['clickIdParam']"
              ></ui-input>

              <div v-if="clickIdParam" class="clickId-text">
                <ui-label
                  :text="t('Click ID parameter')"
                  :help-tooltip="t('With this HTTP GET parameter BeMob Click ID will be passed to affiliate network.')"
                ></ui-label>
                <div>
                  {{ t('According to selected affiliate network settings we already appending BeMob click ID with {0} parameter', [ clickIdParam ]) }}
                </div>
              </div>
            </div>

            <div>
              <ui-select
                v-model="form.currencyId"
                :placeholder="t('Currency')"
                :label="t('Currency')"
                :label-help="t('Set revenue currency for this offer. Offer currency will automatically be converted to your account currency in reports.')"
                :items="currencyListOrderedPreferred"
                item-text="name"
                item-value="code"
                :return-object="false"
                :hide-details="2"
                attach
                :error-messages="errorsDict['currencyId']"
              ></ui-select>
            </div>

            <div class="d-flex">
              <ui-radio-group v-model="form.payoutType" class="w50 mb-16" :label="t('Payout type')">
                <ui-radio :label="t('Auto')" value="auto"></ui-radio>
                <ui-radio :label="t('Manual')" value="manual"></ui-radio>
              </ui-radio-group>
              <div v-show="form.payoutType === 'manual'" class="w50">
                <ui-input
                  :prefix="currencies[form.currencyId]"
                  v-model="form.payoutSum"
                  :label="t('Payout Sum')"
                  :has-errors="!!errorsDict['payoutSum']"
                ></ui-input>
              </div>
            </div>

            <div class="checkbox__wrap">
              <ui-checkbox
                :label="t('Enable daily cap')"
                :help-tooltip="t('Limit the number of conversions per day.')"
                v-model="enableDailyCap"
              ></ui-checkbox>
            </div>

            <div v-show="form.enableDailyCap">
              <div>
                <ui-count-input
                  v-model="form.dailyCapLimit"
                  :label="t('Daily cap limit')"
                  :min="0"
                  :has-errors="!!errorsDict['dailyCapLimit']"
                ></ui-count-input>
              </div>
              <div class="mt-18 mb-6">
                <ui-select
                  v-model="form.dailyCapTimezone"
                  :placeholder="t('Daily cap timezone')"
                  :label="t('Daily cap timezone')"
                  :label-help="t('Timezone for calculating the current date.')"
                  :items="timezoneList"
                  item-text="name"
                  item-value="zone"
                  :return-object="false"
                  :hide-details="2"
                  max-width-calc
                  :error-messages="errorsDict['dailyCapTimezone']"
                ></ui-select>
              </div>

              <div>
                <ui-select
                  v-model="form.dailyCapFailoverOffer"
                  :placeholder="t('Select Offer')"
                  :label="t('Redirect to offer')"
                  :label-help="t('Forward traffic to specified offer when daily cap is reached.')"
                  :items="offerOptionsWithoutEdited"
                  :fetchOptions="fetchOfferOptions"
                  itemText="name"
                  itemValue="id"
                  :return-object="true"
                  :hide-details="2"
                  max-width-calc
                  :error-messages="errorsDict['dailyCapFailoverOfferId']"
                ></ui-select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <section class="justify-space-between align-center xs12" style="display: flex; width: 100%">
        <div>
          <error-aggregator
            v-model="errors"
            v-model:active="errorsControlActive"
          ></error-aggregator>
        </div>
        <div class="d-flex justify-end align-center">
          <ui-update-info v-if="mode === 'edit'" :data="form"></ui-update-info>
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            @click="checkClose"
          ></ui-button>

          <ui-button
            :disabled="isActionsDisabled"
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            :loading="submitLoading"
            v-bind="getIntroParams(4)"
            @click="tutorial.close(); submit();"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import WorkspaceSelect from '@/views/reports/pickers/wokrspace-select.vue';
import NoTagFound from '@/views/reports/views/no-tag-found.vue';
import {
  useOfferDialog,
  type OfferDialogData,
} from './useOfferDialog';

const props = withDefaults(defineProps<{
  data?: OfferDialogData;
}>(), {
  data: () => ({}),
});

const emit = defineEmits<{
  close: [];
}>();

const mode = computed(() => props.data.mode ?? 'create');

const {
  store,

  t,
  show,
  submitLoading,
  form,
  disableWorkspaceSelect,
  errors,
  errorsControlActive,
  errorsDict,
  modalTitle,
  chips,
  countryListPlusGlobal,
  nameDescription,
  workspacesList,
  isActionsDisabled,
  showPlanLimitsWarning_tags,
  affiliatesList,
  clickIdParam,
  currencyListOrderedPreferred,
  currencies,
  enableDailyCap,
  timezoneList,
  offerOptionsWithoutEdited,
  fetchOfferOptions,
  postbackUrl,
  tutorial,
  onChangeWorkspace,
  changeAffiliates,
  checkClose,
  clearTags,
  submit,
  getIntroParams,
} = useOfferDialog(mode.value, () => props.data, () => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.name-description {
  margin-left: 16px;
  word-break: break-all;
  font-style: italic;
  font-size: 12px;
}

.update-info {
  display: flex;

  :deep(.name) {
    max-width: 250px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

@media (max-width: 900px) {
  .update-info {
    display: none;
  }
}
</style>

<style lang="scss">
  @import '@/styles/dialogs/offer-dialog';

  .currency-input {
    &__content {
      > .v-input {
        margin-top: 4px;
      }
    }
  }
</style>
